import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { useParams } from "react-router-dom";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import { showSnackbar } from "@/lib/snackbarUtils";
import { DELETE_PDR_REQUIREMENT_MUTATION, GET_PDR_REQUIREMENTS_QUERY } from "../../queries";
import type { RequirementHistoryRowContext } from "./updateRequirementDatesValidation";

export default function DeleteRequirementDialog({
  open,
  row,
  cycleCode,
  onClose
}: {
  open: boolean;
  row: RequirementHistoryRowContext | null;
  cycleCode: string;
  onClose: () => void;
}) {
  const { pgaId } = useParams();
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setDeleteError(null);
  }, [open]);

  const [deleteRequirement, { loading }] = useMutation(DELETE_PDR_REQUIREMENT_MUTATION, {
    refetchQueries: pgaId
      ? [
          {
            query: GET_PDR_REQUIREMENTS_QUERY,
            variables: { pgaId, cycleCode }
          }
        ]
      : [],
    awaitRefetchQueries: true
  });

  const handleConfirm = async () => {
    if (!row || !pgaId) return;

    setDeleteError(null);

    try {
      const { data } = await deleteRequirement({
        variables: {
          input: {
            pgaId,
            pdpSerial: row.pdpSerial
          }
        }
      });

      if (!data?.deletePdrRequirement?.success) {
        setDeleteError(data?.deletePdrRequirement?.message || "Unable to delete requirement.");
        return;
      }

      showSnackbar(data.deletePdrRequirement.message);
      onClose();
    } catch {
      setDeleteError("Unable to delete requirement.");
    }
  };

  return (
    <Dialog
      open={open}
      headline="Delete Requirement?"
      onClosed={onClose}
      actions={
        <>
          <Button variant="text" onClick={onClose} disabled={loading} testId="delete-pdr-requirement-cancel">
            Cancel
          </Button>
          <Button
            variant="filled"
            theme="error"
            onClick={handleConfirm}
            disabled={loading || !row}
            loading={loading}
            testId="delete-pdr-requirement-confirm"
          >
            Delete
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {deleteError ? (
          <Banner variant="error" description={deleteError} inline closable={false} hideSecondaryDismiss />
        ) : null}
        <p className="text-body-medium">
          This permanently deletes this PDR requirement history row and its linked requirement and stipulation records.
          Activity credits are not removed. This cannot be undone.
        </p>
      </div>
    </Dialog>
  );
}
